import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
	Contractor,
	ContractorMailTemplatePayload,
	ContractorMailTemplateResponse,
	ContractorPatchPayload,
	InternalStatusDto,
	Invoice,
	InvoicePatchPayload,
	MailBulkSendPayload,
	MailPreviewResponse,
} from './vindication.model';
import { Result } from '@/reservation/reservation.model';
import { MessageTemplate, MessageTemplatePlaceholder } from '@/common/message-generate/message-generate.model';

@Injectable({
	providedIn: 'root',
})
export class VindicationApiService {
	private readonly httpClient = inject(HttpClient);
	private readonly debtCollectionUrl = environment.debtCollectionUrl;

	getInvoices(params: string): Observable<Result<Invoice>> {
		return this.httpClient.get<Result<Invoice>>(`${this.debtCollectionUrl}/invoices/${params}`);
	}

	getInternalStatuses(): Observable<InternalStatusDto[]> {
		return this.httpClient
			.get<Result<InternalStatusDto>>(`${this.debtCollectionUrl}/invoices/internal-statuses/`)
			.pipe(map((response) => response.results));
	}

	getContractorInternalStatuses(): Observable<InternalStatusDto[]> {
		return this.httpClient
			.get<Result<InternalStatusDto>>(`${this.debtCollectionUrl}/contractors/internal-statuses/`)
			.pipe(map((response) => response.results));
	}

	getContractors(params: string): Observable<Result<Contractor>> {
		return this.httpClient.get<Result<Contractor>>(`${this.debtCollectionUrl}/contractors/${params}`);
	}

	getContractor(contractorId: number): Observable<Contractor> {
		return this.httpClient.get<Contractor>(`${this.debtCollectionUrl}/contractors/${contractorId}/`);
	}

	patchContractor(id: number, payload: ContractorPatchPayload): Observable<Contractor> {
		return this.httpClient.patch<Contractor>(`${this.debtCollectionUrl}/contractors/${id}/`, payload);
	}

	patchInvoice(id: number, payload: InvoicePatchPayload): Observable<Invoice> {
		return this.httpClient.patch<Invoice>(`${this.debtCollectionUrl}/invoices/${id}/`, payload);
	}

	getMailTemplateList(): Observable<MessageTemplate[]> {
		return this.httpClient
			.get<Result<MessageTemplate>>(`${this.debtCollectionUrl}/mails/templates/`)
			.pipe(map((result) => result?.results));
	}

	getMailTemplatePlaceholders(): Observable<MessageTemplatePlaceholder[]> {
		return this.httpClient
			.get<Result<MessageTemplatePlaceholder>>(`${this.debtCollectionUrl}/mails/placeholders/`)
			.pipe(map((result) => result?.results));
	}

	createMailPreview(payload: {
		invoices: number[];
		message: string;
		subject: string;
	}): Observable<MailPreviewResponse[]> {
		return this.httpClient.post<MailPreviewResponse[]>(`${this.debtCollectionUrl}/invoices/mail_preview/`, {
			...payload,
			template: 1,
		});
	}

	createMailBulkSend(payload: MailBulkSendPayload[]): Observable<MailBulkSendPayload[]> {
		return this.httpClient.post<MailBulkSendPayload[]>(`${this.debtCollectionUrl}/mails/bulk_send/`, payload);
	}

	createMailWithAttachment(payload: MailBulkSendPayload, attachments: File[]): Observable<MailBulkSendPayload> {
		const formData = new FormData();

		formData.append('mails', JSON.stringify(payload.mails));

		formData.append('subject', payload.subject);
		formData.append('message', payload.message);
		formData.append('invoice', String(payload.invoice));

		if (payload.cc && payload.cc.length > 0) {
			formData.append('cc', JSON.stringify(payload.cc));
		}

		attachments.forEach((file, index) => {
			formData.append(`attachment_${index}`, file);
		});

		return this.httpClient.post<MailBulkSendPayload>(`${this.debtCollectionUrl}/mails/with_attachment/`, formData);
	}

	getContractorMailTemplates(contractorId: number): Observable<ContractorMailTemplateResponse[]> {
		return this.httpClient
			.get<
				Result<ContractorMailTemplateResponse>
			>(`${this.debtCollectionUrl}/contractors/contractor_mail_templates/?contractor=${contractorId}`)
			.pipe(map((result) => result?.results));
	}

	createContractorMailTemplate(payload: ContractorMailTemplatePayload): Observable<ContractorMailTemplateResponse> {
		return this.httpClient.post<ContractorMailTemplateResponse>(
			`${this.debtCollectionUrl}/contractors/contractor_mail_templates/`,
			payload,
		);
	}

	updateContractorMailTemplate(
		payload: ContractorMailTemplatePayload,
		contractorMailTemplateId: number,
	): Observable<ContractorMailTemplateResponse> {
		return this.httpClient.put<ContractorMailTemplateResponse>(
			`${this.debtCollectionUrl}/contractors/contractor_mail_templates/${contractorMailTemplateId}/`,
			payload,
		);
	}

	removeContractorMailTemplate(contractorMailTemplateId: number): Observable<ContractorMailTemplateResponse> {
		return this.httpClient.delete<ContractorMailTemplateResponse>(
			`${this.debtCollectionUrl}/contractors/contractor_mail_templates/${contractorMailTemplateId}/`,
		);
	}
}
